import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { Box, Button, Select, MenuItem, TextField, InputLabel, FormControl } from "@mui/material";

export default function StatusEditor({
  status,
  onStatusChange,
  rejectionReason,
  onRejectionReasonChange,
  onSubmit,
}) {
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  const handleUpdate = async () => {
    setUpdating(true);
    await onSubmit(); // parent API call
    setTimeout(() => {
      setOpen(false); // slide up
      setUpdating(false);
    }, 400);
  };

  return (
    <Box>
      <Box className="d-flex align-items-center gap-3 mb-2">
        <StatusPill status={status}>{status}</StatusPill>

        <Button
          variant="outlined"
          size="small"
          sx={{ textTransform: "none" }}
          onClick={() => setOpen(!open)}
        >
          {open ? "Close" : "Edit"}
        </Button>
      </Box>

      {/* ✅ Slide Section */}
      {open && (
        <SlideContainer open={open}>
          <Box className="p-3 border rounded" sx={{ background: "#fafafa", mt: 1 }}>
            <FormControl size="small" sx={{ width: 220, mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => onStatusChange(e.target.value)}
              >
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </Select>
            </FormControl>

            {/* Show textfield if rejected */}
            {status === "Rejected" && (
              <TextField
                size="small"
                label="Reason for Rejection"
                multiline
                rows={4}
                className="w-100 mb-3"
                value={rejectionReason}
                onChange={(e) => onRejectionReasonChange(e.target.value)}
              />
            )}

            <Button
              variant="contained"
              color="warning"
              disabled={updating}
              onClick={handleUpdate}
            >
              {updating ? "Updating..." : "Update"}
            </Button>
          </Box>
        </SlideContainer>
      )}
    </Box>
  );
}

/* ✅ Slide Down Animation */
const slideDown = keyframes`
  from { max-height: 0; opacity: 0; transform: translateY(-10px); }
  to { max-height: 500px; opacity: 1; transform: translateY(0); }
`;

/* ✅ Slide Up Animation */
const slideUp = keyframes`
  from { max-height: 500px; opacity: 1; transform: translateY(0); }
  to { max-height: 0; opacity: 0; transform: translateY(-10px); }
`;

/* ✅ Status Pill Styling */
const StatusPill = styled.span`
  padding: 8px 12px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  display: inline-block;
  min-width: 150px;
  text-align: center; 

  ${({ status }) =>
    status === "Approved" &&
    `
      background: #d4f6d4;
      color: #0a8a0a;
    `}

  ${({ status }) =>
    status === "Pending" &&
    `
      background: #fff3cd;
      color: #c77d00;
    `}

  ${({ status }) =>
    status === "Rejected" &&
    `
      background: #ffe1e1;
      color: #d10000;
    `}
`;

/* ✅ Wrapper for slide container */
const SlideContainer = styled.div`
  overflow: hidden;
  animation: ${({ open }) => (open ? slideDown : slideUp)} 0.35s ease forwards;
`;
